import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';

import AdmissionMapService from '@/services-http/servicos/AdmissionMapService';
import Formatters from '@/shared/formatters/formatters';
import store from '@/store/index';

import i18n from '@/plugins/i18n';
import { store as observableStore } from '@/shared/observable/store';
import {
  AutocompleteOption,
  getBooleanOptions,
  sortHighCostTypeOptions,
} from './admission-map-config-types';
import {
  buildFilterParams,
  buildSearchParams,
  defaultFilter,
  defaultPagination,
  initState,
  normalizeAdmissions,
  normalizeHistory,
} from './admission-map-factory';
import {
  AdmissionMapFilter,
  AdmissionMapHistoryItem,
  AdmissionMapHistoryOptions,
  AdmissionMapItem,
  AdmissionMapModel,
  AdmissionMapPagination,
  ContractOptions,
  Modal,
  TableOptions,
} from './admission-map-types';

@Module({
  namespaced: true,
  name: 'admission-map',
  dynamic: true,
  store,
})
class AdmissionMapStore extends VuexModule {
  data: AdmissionMapModel = initState;

  get filter() {
    return this.data.filter;
  }

  get loadingCarriers() {
    return this.data.loadingCarriers;
  }

  get loadingGroups() {
    return this.data.loadingGroups;
  }

  get loadingContracts() {
    return this.data.loadingContracts;
  }

  get loadingPasswordStatuses() {
    return this.data.loadingPasswordStatuses;
  }

  get loadingPage() {
    return this.data.loadingPage;
  }

  get loadingTable() {
    return this.data.loadingTable;
  }

  get loadingUpdate() {
    return this.data.loadingUpdate;
  }

  get loadingRecordHistories() {
    return this.data.loadingRecordHistories;
  }

  get loadingExport() {
    return this.data.loadingExport;
  }

  get groups() {
    return this.data.groups;
  }

  get carriers() {
    return this.data.carriers;
  }

  get contracts() {
    return this.data.contracts;
  }

  get records() {
    return this.data.records;
  }

  get recordHistories() {
    return this.data.recordHistories;
  }

  get selectedRecords() {
    return this.data.selectedRecords;
  }

  get showSnackbarCustomize() {
    return this.data.showSnackbarCustomize;
  }

  get showHistoryModal() {
    return this.data.showHistoryModal;
  }

  get pagination() {
    return this.data.pagination;
  }

  get currentPage() {
    return this.data.pagination.page;
  }

  get itemsPerPage() {
    return this.data.pagination.itemsPerPage;
  }

  get totalItems() {
    return this.data.pagination.totalItems;
  }

  get sortBy() {
    return this.data.sortBy;
  }

  get sortDesc() {
    return this.data.sortDesc;
  }

  get historyOptions() {
    return getBooleanOptions();
  }

  get highCostOptions() {
    return this.data.highCostTypes;
  }

  get urgencyTypeOptions() {
    return this.data.urgencyTypes;
  }

  get icuOptions() {
    return getBooleanOptions();
  }

  get specialCaseOptions() {
    return getBooleanOptions();
  }

  get unauthorizedOptions() {
    return getBooleanOptions();
  }

  get passwordStatusOptions() {
    return this.data.passwordStatuses;
  }

  get hasFiltersSelected() {
    const {
      financialGroupId,
      carrierId,
      contractId,
      patientName,
      hasRecordHistory,
      passwordStatuses,
      startDate,
      endDate,
    } = this.data.filter;

    return Boolean(
      financialGroupId ||
      carrierId ||
      contractId ||
      patientName ||
      startDate ||
      endDate ||
      typeof hasRecordHistory === 'boolean' ||
      (Array.isArray(passwordStatuses) && passwordStatuses.length > 0)
    );
  }

  get clickAway() {
    return this.data.clickAway;
  }

  @Action
  async initPage() {
    try {
      this.SET_LOADING_PAGE(true);

      await Promise.all([
        this.fetchPasswordStatuses(),
        this.fetchUrgencyTypes(),
        this.fetchHighCostTypes(),
      ]);
    } finally {
      this.SET_LOADING_PAGE(false);
    }
  }

  @Action
  async exportAdmission() {
    const service = new AdmissionMapService();

    try {
      this.SET_EXPORT_LOADING(true);
      const params = buildFilterParams(this.data);
      const passwordStatuses = Array.isArray(this.data.filter.passwordStatuses)
        ? this.data.filter.passwordStatuses
        : [];

      if (passwordStatuses.length > 0) {
        delete params.passwordStatuses;
        params.passwordStatuses = [...passwordStatuses];
      }

      const previousCustomDialogState = observableStore.isCustomDialog;
      observableStore.isCustomDialog = true;
      try {
        await service.export(params);
      } finally {
        observableStore.isCustomDialog = previousCustomDialogState;
      }

      return true;
    } catch {
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.exportError')), show: true });
      return false;
    } finally {
      setTimeout(() => {
        this.SET_EXPORT_LOADING(false);
      }, 1000);
    }
  }

  @Action
  async fetchContracts(contractOption: ContractOptions) {
    const service = new AdmissionMapService();

    try {
      const formatter = new Formatters();
      const params = new URLSearchParams();
      if (contractOption.carrierId) params.append('carrierId', String(contractOption.carrierId));
      if (contractOption.financialGroupId) params.append('financialGroupId', String(contractOption.financialGroupId));

      const { data } = await service.fetchAdmissionContracts(params);
      const contracts = data.content;
      const formatted = (Array.isArray(contracts) ? contracts : []).map((contract: Record<string, any>) => ({
        ...contract,
        id: contract.id,
        text: contract.name,
      })).filter(contract => !formatter.isEmptyValue(contract.id));

      this.SET_CONTRACTS(formatted);
    } catch (error) {
      this.SET_CONTRACTS([]);
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.contractsError')), show: true });
    }
  }

  @Action
  async fetchPasswordStatuses() {
    const service = new AdmissionMapService();

    try {
      this.SET_PASSWORD_STATUSES_LOADING(true);

      const response = await service.fetchPasswordStatuses();
      const payload = response?.data;
      let items = Array.isArray(payload?.content)
        ? payload.content
      : (Array.isArray(payload) ? payload : []);

      if (this.data.filter.carrierId) {
        items = items.filter((item: any) => item.id === this.data.filter.carrierId);
      }


      let normalized: any[] = [];

      items.forEach((item: any) => {
        const carrierGroup = item.status.map((statusName: string) => ({
          group: item.name, name: statusName
        }));

        const groupItems = [
          { header: item.name },
          ...carrierGroup,
        ];

        normalized = [...normalized, ...groupItems];
      });

      this.SET_PASSWORD_STATUSES(normalized);

      return normalized;
    } catch (error) {
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.passwordStatusesError')), show: true });
      return [];
    } finally {
      this.SET_PASSWORD_STATUSES_LOADING(false);
    }
  }

  @Action
  async fetchUrgencyTypes() {
    if (this.data.urgencyTypes.length > 0) return this.data.urgencyTypes;

    const service = new AdmissionMapService();

    try {
      const response = await service.fetchUrgencyTypes();
      const items = Array.isArray(response?.data) ? response.data : [];
      this.SET_URGENCY_TYPES(items);
      return items;
    } catch (error) {
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.urgencyTypesError')), show: true });
      return [];
    }
  }

  @Action
  async fetchHighCostTypes() {
    if (this.data.highCostTypes.length > 0) return this.data.highCostTypes;

    const service = new AdmissionMapService();

    try {
      const response = await service.fetchHighCostTypes();
      const items = sortHighCostTypeOptions(Array.isArray(response?.data) ? response.data : []);
      this.SET_HIGH_COST_TYPES(items);
      return items;
    } catch (error) {
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.highCostTypesError')), show: true });
      return [];
    }
  }

  @Action
  applyFilter(filter: Partial<AdmissionMapFilter>) {
    this.SET_FILTER(filter);
    this.SET_PAGINATION({ page: 1 });
  }

  @Action
  resetFilter() {
    this.RESET_FILTER_STATE();
    this.RESET_CONTRACTS();
    this.clearTableData();
  }

  @Action
  clearTableData() {
    this.CLEAN_RECORDS();
    this.CLEAR_SELECTED_RECORDS();
    this.RESET_PAGINATION_STATE();
  }

  @Action
  async searchByFilter() {
    this.SET_PAGINATION({ page: 1 });
    this.CLEAN_RECORDS();
    await this.search();
  }

  @Action
  async search() {
    if (!this.hasFiltersSelected) return;

    const service = new AdmissionMapService();

    try {
      this.SET_TABLE_LOADING(true);

      const params = buildSearchParams(this.data);
      const response = await service.fetchAdmissions(params);
      const { items, total } = normalizeAdmissions(response?.data);
      this.SET_RECORDS(items);
      this.SET_TOTAL_ITEMS(total);
    } catch (error) {
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.searchError')), show: true });
    } finally {
      this.SET_TABLE_LOADING(false);
    }
  }

  @Action
  async fetchAdmissions(params: Record<string, any>) {
    try {
      const service = new AdmissionMapService();
      return await service.fetchAdmissions(params);
    } catch(error) {
      return error;
    }
  }

  @Action
  async fetchAdmissionHistory(recordId: number) {
    const previousCustomDialogState = observableStore.isCustomDialog;
    observableStore.isCustomDialog = true;
    try {
      this.SET_LOADING_RECORD_HISTORIES(true);

      if (this.data.urgencyTypes.length === 0) {
        await this.fetchUrgencyTypes();
      }
      if (this.data.highCostTypes.length === 0) {
        await this.fetchHighCostTypes();
      }

      const service = new AdmissionMapService();
      const response = await service.fetchAdmissionHistory(recordId);

      const historyOptions: AdmissionMapHistoryOptions = {
        highCostTypes: this.data.highCostTypes,
        urgencyTypes: this.data.urgencyTypes,
      };
      const historyNormalized = normalizeHistory(response.data, historyOptions);
      this.SET_RECORD_HISTORIES(historyNormalized);

      this.showHistory({show: true});
    } catch(error) {
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.historyError')), show: true });
    } finally {
      this.SET_LOADING_RECORD_HISTORIES(false);
      observableStore.isCustomDialog = previousCustomDialogState;
    }
  }

  @Action
  selectRecord(record: AdmissionMapItem | null) {
    if (record) {
      this.SET_SELECTED_RECORD(record);
    } else {
      this.CLEAR_SELECTED_RECORDS();
    }
  }

  @Action
  async updateRecord(record: AdmissionMapItem, position: number) {
    const previousCustomDialogState = observableStore.isCustomDialog;
    observableStore.isCustomDialog = true;
    try {
      this.SET_LOADING_UPDATE(true);

      const service = new AdmissionMapService();

      const allowedFields = [
        'cidCode',
        'cidDescription',
        'speciality',
        'highCost',
        'unauthorized',
        'icu',
        'medicalReport',
        'evolution',
        'note',
        'urgencyType',
        'specialCase',
      ];

      const payload = Object.fromEntries(
        Object.entries(record)
          .filter(([key]) => allowedFields.includes(key))
      );

      this.UPDATE_RECORD(record);

      const response = await service.updateAdmission(record.id, payload);

      return response && response.status ? response.status : false;
    } catch (error) {
      this.showSnackbar({ type: 'error', message: String(i18n.t('admissionMap.messages.updateError')), show: true, top: position });
      return error;
    } finally {
      this.SET_LOADING_UPDATE(false);
      observableStore.isCustomDialog = previousCustomDialogState;
    }
  }

  @Action
  updateTableOptions(options: TableOptions) {
    const nextPage = options?.page ?? this.data.pagination.page;
    const nextItemsPerPage = options?.itemsPerPage ?? this.data.pagination.itemsPerPage;
    const nextSortBy = options?.sortBy && options.sortBy.length > 0 ? options.sortBy[0] : this.data.sortBy;
    const nextSortDesc = options?.sortDesc && options.sortDesc.length > 0 ? Boolean(options.sortDesc[0]) : this.data.sortDesc;

    const hasChanged =
      nextPage !== this.data.pagination.page ||
      nextItemsPerPage !== this.data.pagination.itemsPerPage ||
      nextSortBy !== this.data.sortBy ||
      nextSortDesc !== this.data.sortDesc;

    if (!hasChanged) return;

    this.SET_PAGINATION({ page: nextPage, itemsPerPage: nextItemsPerPage });
    this.SET_SORTING({ sortBy: nextSortBy, sortDesc: nextSortDesc });
    this.search();
  }

  @Mutation
  private SET_LOADING_PAGE(loading: boolean) {
    this.data.loadingPage = loading;
  }

  @Mutation
  private SET_TABLE_LOADING(loading: boolean) {
    this.data.loadingTable = loading;
  }

  @Mutation
  private SET_GROUPS(groups: any[]) {
    this.data.groups = groups;
  }

  @Mutation
  SET_GROUPS_LOADING(loading: boolean) {
    this.data.loadingGroups = loading;
  }

  @Mutation
  private SET_CARRIERS(carriers: any[]) {
    this.data.carriers = carriers;
  }

  @Mutation
  SET_CARRIERS_LOADING(loading: boolean) {
    this.data.loadingCarriers = loading;
  }

  @Mutation
  private SET_CONTRACTS(contracts: any[]) {
    this.data.contracts = contracts;
  }

  @Mutation
  SET_CONTRACTS_LOADING(loading: boolean) {
    this.data.loadingContracts = loading;
  }

  @Mutation
  private RESET_CONTRACTS() {
    this.data.contracts = [];
    this.data.filter.contractId = null;
  }

  @Mutation
  private SET_PASSWORD_STATUSES_LOADING(loading: boolean) {
    this.data.loadingPasswordStatuses = loading;
  }

  @Mutation
  private SET_PASSWORD_STATUSES(passwordStatuses: AutocompleteOption[]) {
    this.data.passwordStatuses = passwordStatuses;
  }

  @Mutation
  private SET_URGENCY_TYPES(urgencyTypes: AutocompleteOption<string>[]) {
    this.data.urgencyTypes = urgencyTypes;
  }

  @Mutation
  private SET_HIGH_COST_TYPES(highCostTypes: AutocompleteOption<string>[]) {
    this.data.highCostTypes = highCostTypes;
  }

  @Mutation
  private SET_FILTER(filter: Partial<AdmissionMapFilter>) {
    this.data.filter = {
      ...this.data.filter,
      ...filter,
    };
  }

  @Mutation
  private RESET_FILTER_STATE() {
    this.data.filter = defaultFilter();
  }

  @Mutation
  private SET_PAGINATION(pagination: Partial<AdmissionMapPagination>) {
    this.data.pagination = {
      ...this.data.pagination,
      ...pagination,
    };
  }

  @Mutation
  private RESET_PAGINATION_STATE() {
    this.data.pagination = defaultPagination();
  }

  @Mutation
  private CLEAN_RECORDS() {
    this.data.records = [];
  }

  @Mutation
  private SET_RECORDS(records: AdmissionMapItem[]) {
    this.data.records = [...this.data.records, ...records];
  }

  @Mutation
  private SET_TOTAL_ITEMS(total: number) {
    this.data.pagination.totalItems = total;
  }

  @Mutation
  private SET_SORTING(payload: { sortBy: string | null; sortDesc: boolean }) {
    this.data.sortBy = payload.sortBy;
    this.data.sortDesc = payload.sortDesc;
  }

  @Mutation
  private SET_SELECTED_RECORD(record: AdmissionMapItem) {
    const selectedRecordsIndex = this.data.selectedRecords.findIndex(existing => existing.id === record.id);

    if (selectedRecordsIndex >=0) {
      this.data.selectedRecords.splice(selectedRecordsIndex, 1, record);
    } else {
      this.data.selectedRecords.push(record);
    }
  }

  @Mutation
  private UPDATE_RECORD(record: AdmissionMapItem) {
    const selectedRecordsIndex = this.data.selectedRecords.findIndex(existing => existing.id === record.id);
    const recordIndex = this.data.records.findIndex(existing => existing.id === record.id);

    if (recordIndex >= 0) {
      this.data.records.splice(recordIndex, 1, record);
      this.data.selectedRecords.splice(selectedRecordsIndex, 1);
    }
  }

  @Mutation
  private SET_LOADING_UPDATE(loading: boolean) {
    this.data.loadingUpdate = loading;
  }

  @Mutation
  private CLEAR_SELECTED_RECORDS(rowId?: AdmissionMapItem['id']) {
    if (rowId !== undefined && rowId !== null) {
      this.data.selectedRecords = this.data.selectedRecords.filter((record: AdmissionMapItem) => record.id !== rowId);
    } else {
      this.data.selectedRecords = [];
    }
  }

  @Mutation
  private SET_EXPORT_LOADING(loading: boolean) {
    this.data.loadingExport = loading;
  }

  @Mutation
  private SET_RECORD_HISTORIES(histories: AdmissionMapHistoryItem[]) {
    this.data.recordHistories = histories;
  }

  @Mutation
  private SET_LOADING_RECORD_HISTORIES(loading: boolean) {
    this.data.loadingRecordHistories = loading;
  }

  @Mutation
  resetRecordHistories() {
    this.data.recordHistories = [];
  }

  @Mutation
  showSnackbar(modalParams: Modal) {
    this.data.showSnackbarCustomize = modalParams;

    if (modalParams.show) {
      setTimeout(() => {
        this.data.showSnackbarCustomize = {
          type: null,
          top: null,
          message: '',
          show: false,
        };
      }, 5000);
    }
  }

  @Mutation
  showHistory(modalParams: Modal) {
    this.data.showHistoryModal = modalParams;
  }

  @Mutation
  onClickAway() {
    this.data.clickAway = true;

    setTimeout(() => {
      this.data.clickAway = false;
    }, 1000);
  }
}

export default getModule(AdmissionMapStore);
